// 🔧 firestore_write_provider.js
// ✅ Purpose: Write-only Firestore provider for the Communication Engine
// ✅ Features:
// - Saves editable content items to /content_items
// - Saves runtime bundles to /content_bundles
// - Saves manifests to /content_manifests
// - Writes audit entries to /content_audit_log
// - Keeps Firestore implementation out of services, hooks, and pages

import {
  addDoc,
  collection,
  doc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";

import { db } from "../../firebase";

/**
 * Saves one editable Content Item.
 */
export async function saveContentItem(contentItem = {}) {
  if (!contentItem.key) {
    throw new Error(
      "Communication Engine content items require a key."
    );
  }

  console.log(
    "[Communication Engine] Saving content item:",
    contentItem.key
  );

  const documentRef = doc(
    db,
    "content_items",
    contentItem.key
  );

  await setDoc(
    documentRef,
    {
      ...contentItem,
      updatedAt: serverTimestamp(),
    },
    {
      merge: true,
    }
  );

  return contentItem.key;
}

/**
 * Saves one published runtime bundle.
 */
export async function savePublishedBundle(
  bundleKey,
  bundle = {}
) {
  if (!bundleKey) {
    throw new Error(
      "Communication Engine bundles require a bundle key."
    );
  }

  console.log(
    "[Communication Engine] Saving bundle:",
    bundleKey
  );

  const documentRef = doc(
    db,
    "content_bundles",
    bundleKey
  );

  await setDoc(
    documentRef,
    {
      ...bundle,
      updatedAt: serverTimestamp(),
    },
    {
      merge: true,
    }
  );

  return bundleKey;
}

/**
 * Canonical bundle save API.
 */
export async function saveContentBundle(
  bundleKey,
  bundle
) {
  return savePublishedBundle(
    bundleKey,
    bundle
  );
}

/**
 * Saves one content manifest.
 */
export async function saveContentManifest(
  manifestKey,
  manifest = {}
) {
  if (!manifestKey) {
    throw new Error(
      "Communication Engine manifests require a manifest key."
    );
  }

  const documentRef = doc(
    db,
    "content_manifests",
    manifestKey
  );

  await setDoc(
    documentRef,
    {
      ...manifest,
      updatedAt: serverTimestamp(),
    },
    {
      merge: true,
    }
  );

  return manifestKey;
}

/**
 * Writes one audit log entry.
 */
export async function writeAuditEntry(auditEntry = {}) {
  const auditRef = collection(
    db,
    "content_audit_log"
  );

  const result = await addDoc(auditRef, {
    ...auditEntry,
    createdAt: serverTimestamp(),
  });

  return result.id;
}